
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Trophy, Users, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface TournamentGroupSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const TournamentGroupSelectionDialog = ({ open, onOpenChange }: TournamentGroupSelectionDialogProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: adminGroups, isLoading } = useQuery({
    queryKey: ['admin-groups', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('group_members')
        .select(`
          group_id,
          groups (
            id,
            name,
            description,
            location,
            avatar_url,
            created_at
          )
        `)
        .eq('user_id', user.id)
        .eq('is_admin', true);
      
      if (error) throw error;
      
      return data?.map(item => item.groups).filter(Boolean) || [];
    },
    enabled: !!user && open
  });

  const handleGroupSelect = (groupId: string) => {
    // Pass the current location as context for proper back navigation
    navigate(`/groups/${groupId}/create-tournament`, {
      state: { 
        fromPage: window.location.pathname,
        fromTitle: window.location.pathname === '/' ? 'Home' : 'Previous Page'
      }
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-yellow-500" />
            Create Tournament
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          <p className="text-sm text-muted-foreground mb-4">
            Select a group where you're an admin to create a new tournament:
          </p>
          
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-200 rounded-lg h-20"></div>
                </div>
              ))}
            </div>
          ) : adminGroups && adminGroups.length > 0 ? (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {adminGroups.map((group) => (
                <Card key={group.id} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => handleGroupSelect(group.id)}>
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-3">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={group.avatar_url} />
                        <AvatarFallback className="bg-primary/10">
                          <Users className="h-6 w-6 text-primary" />
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-sm truncate">{group.name}</h3>
                        {group.description && (
                          <p className="text-xs text-muted-foreground truncate mt-1">
                            {group.description}
                          </p>
                        )}
                        {group.location && (
                          <div className="flex items-center gap-1 mt-1">
                            <MapPin className="h-3 w-3 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground truncate">
                              {group.location}
                            </span>
                          </div>
                        )}
                      </div>
                      <Button size="sm" variant="outline">
                        Create
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Trophy className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
              <h3 className="text-sm font-medium mb-1">No Admin Groups Found</h3>
              <p className="text-xs text-muted-foreground mb-4">
                You need to be an admin of a group to create tournaments.
              </p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  onOpenChange(false);
                  navigate('/groups');
                }}
              >
                Explore Groups
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
